package fr.frux.shared.domain.session

import dev.zacsweers.metro.Inject
import dev.zacsweers.metro.SingleIn
import fr.frux.core.common.auth.SessionExpiryNotifier
import fr.frux.core.common.di.AppScope
import fr.frux.core.common.error.AppError
import fr.frux.core.common.logging.Logger
import fr.frux.shared.domain.repository.AuthRepository
import fr.frux.shared.domain.repository.VerifyOutcome
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow

/**
 * Observable single source of truth for the user session.
 *
 * All auth transitions (restore at startup, code request/verification, logout,
 * expiration) go through this store so every screen sees the same state.
 */
@SingleIn(AppScope::class)
@Inject
class SessionStore(
    private val authRepository: AuthRepository,
    // Sans valeur par défaut, délibérément : un défaut fabriquerait ici une seconde instance,
    // muette, et la panne ne se verrait qu'en production — les tests, eux, resteraient verts.
    private val sessionExpiryNotifier: SessionExpiryNotifier,
) {
    private val _state = MutableStateFlow<SessionState>(SessionState.Unknown)
    val state: StateFlow<SessionState> = _state.asStateFlow()

    /**
     * Restore the session from persisted credentials at startup.
     *
     * - no stored token → [SessionState.Anonymous]
     * - token accepted by the backend → [SessionState.Authenticated]
     * - token rejected (401 mapped to AppError.Unauthorized) → credentials cleared, [SessionState.Expired]
     * - network failure → [SessionState.Authenticated] with [AuthRepository.lastKnownUser] if one is
     *   known (the token is the authority until revoked, #657), else [SessionState.Anonymous]. The token is kept
     */
    suspend fun restore() {
        if (!authRepository.isAuthenticated()) {
            // Résout les droits à « aucun » sans appel réseau (#527) : sans cela ils restent non
            // résolus, et la publicité fermée, pour toute personne qui n'est pas connectée.
            authRepository.getCurrentUser()
            _state.value = SessionState.Anonymous
            return
        }
        authRepository.getCurrentUser().fold(
            onSuccess = { user ->
                _state.value = user
                    ?.let { SessionState.Authenticated(it) }
                    ?: SessionState.Anonymous
            },
            onFailure = { error ->
                if (error is AppError.Unauthorized) {
                    authRepository.logout()
                    _state.value = SessionState.Expired
                } else {
                    // Offline or backend down: don't destroy the session. The local token stays valid
                    // until revoked, so serve the last identity the server confirmed (#657) and only
                    // fall back to anonymous browsing when none is known.
                    _state.value = authRepository
                        .lastKnownUser()
                        ?.let { SessionState.Authenticated(it) }
                        ?: SessionState.Anonymous
                }
            },
        )
    }

    /**
     * Demande l'envoi d'un code à [email] (ADR 0012). N'ouvre pas de session. [purpose] borne ce
     * que le code prouvera (#274) : `null` pour se connecter, `"DELETE_ACCOUNT"` pour confirmer
     * une suppression de compte. La valeur de succès est le code échoé en mode dev, null en
     * production (cf. [AuthRepository.requestCode]).
     */
    suspend fun requestCode(
        email: String,
        purpose: String? = null,
    ): Result<String?> = authRepository.requestCode(email, purpose)

    /**
     * Vérifie [code] pour [email] et, sur [VerifyOutcome.Success], ouvre la session — c'est le
     * seul point d'entrée en [SessionState.Authenticated], comme l'était `login` avant l'ADR 0012.
     */
    suspend fun verifyCode(
        email: String,
        code: String,
        username: String? = null,
    ): Result<VerifyOutcome> =
        authRepository.verifyCode(email, code, username).onSuccess { outcome ->
            if (outcome is VerifyOutcome.Success) {
                _state.value = SessionState.Authenticated(outcome.user)
                // La réponse de verify-code ne porte pas les droits Frux+ (#454) : sans ce relais,
                // un abonné qui se reconnecte verrait la publicité jusqu'au prochain démarrage.
                // Best-effort, la session est déjà ouverte, un échec ici ne la remet pas en cause.
                authRepository
                    .getCurrentUser()
                    .onFailure { error ->
                        Logger.i(TAG, "Entitlements refresh after login failed: ${error::class.simpleName}")
                    }
            }
        }

    /**
     * Log out the current user.
     *
     * The intent to disconnect always wins: [SessionState] moves to [SessionState.Anonymous]
     * even if [AuthRepository.logout] fails to clear the stored token (e.g. storage error) —
     * a stale local token with no matching session is a lesser problem than a UI stuck showing
     * the user as logged in after they explicitly asked to log out. The underlying failure is
     * still logged and returned so callers can inform the user the cleanup wasn't clean.
     */
    suspend fun logout(): Result<Unit> =
        authRepository
            .logout()
            .onFailure { error -> Logger.e(TAG, "Logout: token cleanup failed", error) }
            .also { _state.value = SessionState.Anonymous }

    /**
     * « Déconnecter tous les appareils » (#26, ADR 0019) : contrairement à [logout], l'état ne
     * bascule en [SessionState.Anonymous] qu'après confirmation serveur — [AuthRepository.logoutAll]
     * ne purge lui-même qu'en cas de succès, laisser croire à l'utilisateur que ses autres
     * appareils sont déconnectés alors que l'appel a échoué serait pire qu'un message d'erreur.
     */
    suspend fun logoutAll(): Result<Unit> =
        authRepository
            .logoutAll()
            .onSuccess { _state.value = SessionState.Anonymous }
            .onFailure { error -> Logger.e(TAG, "Logout all: server call failed", error) }

    /**
     * Collecte, jusqu'à annulation, les expirations signalées par la couche réseau (#48).
     * Appelé une fois par App : le store n'a pas de scope propre et ne s'en voit pas attribuer un
     * pour ça — sa durée de vie serait alors celle du graphe, sans personne pour l'annuler.
     */
    suspend fun observeSessionExpiry() {
        sessionExpiryNotifier.events.collect { onSessionExpired() }
    }

    /**
     * Called when any API call is rejected with 401 mid-session.
     *
     * Le nettoyage des credentials est **inconditionnel** : un token rejeté doit disparaître même
     * hors session ouverte. C'est le cas du [restore] hors ligne, qui va en [SessionState.Anonymous]
     * en conservant délibérément le token — si celui-ci est mort, rien d'autre ne le purgerait et
     * chaque requête protégée continuerait de prendre un 401. Purger deux fois est sans effet.
     *
     * Seule la **transition d'état** est réservée à une session en cours. Sinon un 401 pendant
     * [restore], déjà traité sur place, produirait un second changement d'état pour le même
     * incident — et un utilisateur anonyme se verrait annoncer une session expirée qu'il n'a
     * jamais ouverte.
     *
     * Événement périmé (#26, C4b) : `TokenRefresher` purge le stockage *avant* de notifier
     * l'expiration, donc entre l'émission et cette consommation l'utilisateur a pu se reconnecter
     * — une nouvelle paire est alors déjà en stockage. Dans ce cas l'événement ne décrit plus rien
     * de réel : le purger détruirait la session neuve, et l'annoncer comme expirée mentirait à un
     * utilisateur qui vient de se reconnecter avec succès.
     */
    suspend fun onSessionExpired() {
        if (authRepository.isAuthenticated()) {
            return
        }
        authRepository.logout()
        if (_state.value is SessionState.Authenticated) {
            _state.value = SessionState.Expired
        }
    }

    private companion object {
        const val TAG = "SessionStore"
    }
}
